<template>
    <div class="bg-gray-50 px-4 py-8 sm:py-12 sm:px-6 lg:px-8 xl:px-12 w-full sm:w-160 lg:w-240">
        <div class="flex flex-col h-full">
            <h3 class="text-sm font-medium tracking-wide text-gray-500 uppercase">
                {{ $l({ en: "Templates", pl: "Szablony" }) }}
            </h3>
            <div class="hidden lg:flex relative">
                <c-select
                    class="mt-6 flex-shrink-0"
                    :placeholder="$l({ en: 'Sort By', pl: 'Sortuj wg' })"
                    :list="allSorters.map((x) => x.name)"
                    :value="
                        allSorters.find((x) => x.isUsed)
                            ? allSorters.find((x) => x.isUsed).name
                            : ''
                    "
                    @change="toggleSorter($event)"
                />
                <FormInput
                    class="ml-2 mt-5 flex-shrink-0"
                    :placeholder="
                        $l({
                            en: 'Search by name and description',
                            pl: 'Szukaj po nazwie i opisie',
                        })
                    "
                    v-model="queryName"
                />

                <c-multi-select
                    class="w-auto md:flex-grow md:flex-shrink ml-2 multi-sizes"
                    :placeholder="
                        $l({
                            en: 'Filter by template sizes',
                            pl: 'Filtruj po rozmiarach szablonów',
                        })
                    "
                    :list="allSizes"
                    :values="querySizes"
                    :settings="false"
                    @toggleSelection="toggleSize($event)"
                />
            </div>

            <ul
                v-if="selectedCategory.id"
                role="list"
                class="mt-6 overflow-y-auto no-scrollbar grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
                <p
                    class="col-span-3 h-full flex items-center justify-center text-gray-500"
                    v-if="!tempsInCategory.length"
                >
                    {{
                        $l({
                            en: "Unfortunately, there are no templates",
                            pl: "Niestety w tej kategorii",
                        })
                    }}
                    <br />

                    {{
                        $l({
                            en: "available in this category yet",
                            pl: "jeszcze nie ma dostępnych szablonów",
                        })
                    }}
                </p>
                <p
                    class="col-span-3 h-full text-gray-500"
                    v-if="tempsInCategory.length && !sortedTemplates.length"
                >
                    {{
                        $l({
                            en: "Unfortunately, none of the templates meet yours criteria.",
                            pl: "Niestety, żaden szablon nie spełnia Twoich kryteriów.",
                        })
                    }}
                    <br />
                    {{ $l({ en: "Change them or", pl: "Zmień je lub" }) }}

                    <a href="" class="text-green-500 underline" @click="clearFilters">
                        {{ $l({ en: "clear filters", pl: "wyczyść filtry" }) }}
                    </a>
                </p>
                <li class="h-full" v-for="template in sortedTemplates" :key="template.id">
                    <div
                        @click="$emit('selectTemplate', template)"
                        class="h-full cursor-pointer relative p-3 flex flex-col rounded-lg border border-gray- 100 hover:border-gray-300 hover:bg-gray-100 transition ease-in-out duration-150"
                    >
                        <div class="hidden sm:block absolute top-2 right-2 z-10">
                            <button
                                @click.stop="addTemplateToFavs(template.uuid)"
                                type="button"
                                :class="[
                                    favTemplates.indexOf(template.uuid) == -1
                                        ? 'text-gray-300 hover:text-gray-500'
                                        : 'text-green-500 hover:text-green-600',
                                ]"
                                class="p-2"
                            >
                                <span class="sr-only">Close</span>
                                <LoveIcon class="h-6 w-6" />
                                <span>{{ template.likes }}</span>
                            </button>
                        </div>
                        <div
                            class="hidden sm:flex w-48 h-32 flex-shrink-0 mx-auto text-gray-200 items-center justify-center"
                        >
                            <img
                                v-if="template.img"
                                class="object-cover max-w-full max-h-full"
                                :src="template.img"
                                alt=""
                            />
                            <LayoutWindowIcon v-else class="w-32 h-32" />
                        </div>
                        <div class="min-w-0 flex-1 mt-3 text-left">
                            <h4 class="text-base font-medium text-gray-900">
                                {{ template.name }}
                            </h4>
                            <p class="mt-1 mb-2 text-sm text-gray-500">
                                {{ template.desc }}
                            </p>
                            <span
                                v-for="size in template.sizes"
                                :key="size.id"
                                class="mr-1 inline-flex items-center px-1 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200"
                            >
                                {{ size.width }}x{{ size.height }}
                            </span>
                        </div>
                    </div>
                </li>
            </ul>
            <p class="h-full flex items-center justify-center text-gray-500" v-else>
                Wybierz kategorię
                <br />
                aby zobaczyć dostępne szablony
            </p>
        </div>
    </div>
</template>
<script>
import { LoveIcon, LayoutWindowIcon } from "@easy/ui/icons";
import { mapState, mapActions } from "vuex";
import cMultiSelect from "@/apps/banner/components/cMultiSelect";
import cSelect from "@/apps/banner/components/cSelect";
import { FormInput } from "@easy/ui/forms";

export default {
    name: "Templates",
    props: {
        selectedCategory: Object,
    },
    emits: ["selectTemplate"],
    data() {
        return {
            querySizes: [],
            queryName: "",

            allSorters: [
                {
                    value: "new",
                    name: "Od najnowszych",
                    isUsed: false,
                },
                {
                    value: "popular",
                    name: "Wg popularności",
                    isUsed: false,
                },
                {
                    value: "name",
                    name: "Po nazwie",
                    isUsed: false,
                },
            ],
        };
    },
    computed: {
        ...mapState("banner", ["templates", "favTemplates"]),
        allSizes() {
            let all = [];
            this.tempsInCategory.forEach((y) => {
                const sizes = y.sizes.map((x) => `${x.width}x${x.height}`);
                all = all.concat(all, sizes);
            });
            const all2 = all.filter((item, pos) => all.indexOf(item) === pos);
            return all2;
        },
        tempsInCategory() {
            //Favorites category id == 7
            if (this.selectedCategory.id == 7) {
                return this.templates.filter((x) => this.favTemplates.indexOf(x.uuid) !== -1);
            } else {
                return this.templates.filter((x) => x.categoryId == this.selectedCategory.id);
            }
        },
        filterTemplates() {
            const tempsByName = this.filterByName(this.tempsInCategory);
            const tempsyBySizes = this.filterBySizes(tempsByName);
            return tempsyBySizes;
        },
        sortedTemplates() {
            const selectedSorter = this.allSorters.find((x) => x.isUsed);
            if (!selectedSorter) {
                return this.filterTemplates;
            }
            if (selectedSorter.value === "new") {
                return this.sortByDate(this.filterTemplates);
            }
            if (selectedSorter.value === "name") {
                return this.sortByName(this.filterTemplates);
            }
            if (selectedSorter.value === "popular") {
                return this.sortByLikes(this.filterTemplates);
            }
            return true;
        },
    },
    components: {
        LoveIcon,
        LayoutWindowIcon,
        cMultiSelect,
        cSelect,
        FormInput,
    },
    methods: {
        ...mapActions("banner", ["updateFavTemplates"]),
        addTemplateToFavs(id) {
            this.updateFavTemplates(id);
        },
        toggleSize(size) {
            const index = this.querySizes.indexOf(size);
            if (index == -1) {
                this.querySizes.push(size);
            } else {
                this.querySizes.splice(index, 1);
            }
        },
        toggleSorter(name) {
            this.allSorters.forEach((x) => {
                if (x.name === name) {
                    x.isUsed = true;
                } else {
                    x.isUsed = false;
                }
            });
        },
        filterByName(templates) {
            let filtered = [];
            if (this.queryName.length) {
                filtered = templates.filter(
                    (x) =>
                        x.name.toLowerCase().indexOf(this.queryName.toLowerCase()) !== -1 ||
                        (x.desc
                            ? x.desc.toLowerCase().indexOf(this.queryName.toLowerCase()) !== -1
                            : false)
                );
            } else {
                filtered = templates;
            }
            return filtered;
        },
        filterBySize(templates, size) {
            const filtered = [];
            templates.forEach((t) => {
                if (t.sizes.map((y) => `${y.width}x${y.height}`).indexOf(size) !== -1) {
                    filtered.push(t);
                }
            });
            return filtered;
        },
        filterBySizes(templates) {
            let filtered = [];
            if (this.querySizes.length) {
                this.querySizes.forEach((querySize) => {
                    const filteredBySize = this.filterBySize(templates, querySize);
                    filtered = filtered.concat(filteredBySize);
                });
            } else {
                filtered = templates;
            }
            //remove duplicates
            filtered = filtered.filter(
                (value, index, self) => index === self.findIndex((t) => t.id === value.id)
            );
            return filtered;
        },
        sortByName(templates) {
            return templates.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
        },
        sortByDate(templates) {
            return templates.sort((a, b) =>
                a.createdAt > b.createdAt ? -1 : b.createdAt > a.createdAt ? 1 : 0
            );
        },
        sortByLikes(templates) {
            return templates.sort((a, b) => (a.likes > b.likes ? -1 : b.likes > a.likes ? 1 : 0));
        },
        clearFilters() {
            this.querySizes = [];
            this.queryName = "";
        },
    },
};
</script>
